/**
 * AreaPage — shared component for metro and state landing pages.
 *
 * Renders:
 *   - SEO meta + canonical
 *   - LocalBusiness JSON-LD with areaServed (state + counties + MSAs)
 *   - FAQPage JSON-LD stacked
 *   - Hero with eyebrow + h1 + intro
 *   - Office card(s) for offices serving this area
 *   - Cities grid (named, scannable, AEO-friendly)
 *   - Counties served strip
 *   - Body sections (state pages have richer content)
 *   - FAQ accordion (area-specific Qs)
 *   - Phone-first CTA (no auto-schedule push)
 */

import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { MapPin, Phone, ArrowUpRight } from 'lucide-react';
import { METRO_AREAS, STATE_PAGES, ALL_OFFICES } from './areaData';
import { useJsonLd } from '@/hooks/useJsonLd';

function findArea(slug) {
  return METRO_AREAS[slug] || STATE_PAGES[slug] || null;
}

function buildAreaSchema(area) {
  const offices = (area.serviceOffices || []).map((id) => (ALL_OFFICES || []).find((o) => o.id === id)).filter(Boolean);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...(offices || []).map((o) => ({
        '@type': 'AccountingService',
        '@id': `${area.canonical}#${o.id}`,
        name: `Kohari Gonzalez Oneyear & Brown PLLC — ${o.label}`,
        url: area.canonical,
        image: 'https://kgob.com/share/og/v1/kgob-share-default.png',
        telephone: o.phone,
        priceRange: '$$$',
        address: { '@type': 'PostalAddress', streetAddress: o.address.split(',')[0], addressLocality: o.address.split(',')[1]?.trim(), addressRegion: 'NC', postalCode: (o.address.match(/\d{5}/) || [''])[0], addressCountry: 'US' },
        hasMap: o.maps,
        areaServed: [
          ...(area.type === 'metro' ? [{ '@type': 'AdministrativeArea', name: area.msa }] : []),
          ...(area.counties || []).map((c) => ({ '@type': 'AdministrativeArea', name: `${c}${c.includes('(SC)') ? '' : area.state ? '' : ''}` })),
          ...(area.cities || []).slice(0, 50).map((c) => ({ '@type': 'City', name: c })),
          { '@type': 'State', name: 'North Carolina' },
          { '@type': 'State', name: 'South Carolina' },
        ],
        parentOrganization: {
          '@type': 'AccountingService',
          name: 'Kohari Gonzalez Oneyear & Brown PLLC',
          url: 'https://kgob.com',
          areaServed: { '@type': 'Country', name: 'United States' },
        },
      })),
      ...(area.faqs && area.faqs.length
        ? [
            {
              '@type': 'FAQPage',
              '@id': `${area.canonical}#faq`,
              mainEntity: (area.faqs || []).map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            },
          ]
        : []),
    ],
  };
}

// Legacy office slugs crawled by Google — redirect to the covering metro page
const LEGACY_OFFICE_REDIRECTS = {
  charlotte: '/locations/charlotte-metro',
  southpark: '/locations/charlotte-metro',
  mooresville: '/locations/charlotte-metro',
  'winston-salem': '/locations/triad',
  pittsburgh: '/locations',
};

export default function AreaPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const area = findArea(slug);

  // Imperatively inject JSON-LD schema (Helmet v2 silently drops <script> content)
  useJsonLd(`area-${slug || 'unknown'}`, area ? buildAreaSchema(area) : null);

  // State pages canonicalize at top-level paths; redirect the /locations/ variant
  if (!slugProp && area && area.canonical && !area.canonical.includes('/locations/')) {
    return <Navigate to={`/${slug}`} replace />;
  }

  if (!area) {
    const target = LEGACY_OFFICE_REDIRECTS[slug];
    if (target) return <Navigate to={target} replace />;
    return (
      <div className="min-h-screen bg-[#0B2240] text-white">
        <Helmet>
          <title>Location Not Found | KGOB CPAs & Advisors</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-32">
          <h1 className="text-3xl font-bold mb-4">Page not found</h1>
          <p className="text-white/70">The location page you requested does not exist. <Link data-testid="area-page-link-1" to="/locations" className="text-[#D4AF37] hover:underline">View all locations →</Link></p>
        </main>
        <Footer />
      </div>
    );
  }

  const offices = (area.serviceOffices || []).map((id) => (ALL_OFFICES || []).find((o) => o.id === id)).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0B2240] text-white" data-testid={`area-page-${area.slug}`}>
      <Helmet>
        <title>{area.metaTitle}</title>
        <meta name="description" content={area.metaDescription} />
        <link rel="canonical" href={area.canonical} />
        <meta property="og:title" content={area.metaTitle} />
        <meta property="og:description" content={area.metaDescription} />
        <meta property="og:url" content={area.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://kgob.com/share/og/v1/kgob-share-default.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={area.metaTitle} />
        <meta name="twitter:description" content={area.metaDescription} />
        <meta name="twitter:image" content="https://kgob.com/share/og/v1/kgob-twitter-card.png" />
      </Helmet>

      <Header />

      <main className="pb-24">
        {/* Hero */}
        <section className="px-4 sm:px-6 lg:px-8 pt-16 pb-12 max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p
              className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#D4AF37] mb-5"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
              data-testid="area-eyebrow"
            >
              {area.eyebrow}
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {area.h1}<span className="text-[#D4AF37]">.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/70 mt-6 leading-relaxed max-w-3xl font-serif">
              {area.intro}
            </p>
          </div>
        </section>

        {/* Offices serving this area */}
        {offices.length > 0 && (
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-16">
            <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37] mb-5" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
              {offices.length === 1 ? 'Anchor office' : 'Anchor offices'}
            </h2>
            <div className={`grid ${offices.length === 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-' + Math.min(offices.length, 3)} gap-6`}>
              {(offices || []).map((o) => (
                <div key={o.id} className="bg-[#0F2A4D] border border-white/8 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors">
                  <div className="flex items-start gap-3 mb-3">
                    <MapPin size={18} className="mt-1 text-[#D4AF37] flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>{o.label}</h3>
                      <p className="text-sm text-white/70 mt-1">{o.address}</p>
                    </div>
                  </div>
                  <a data-testid="area-page-a-2" href={`tel:${o.phone}`} className="inline-flex items-center gap-2 text-white/85 hover:text-[#D4AF37] text-sm transition-colors mb-3">
                    <Phone size={14} className="text-[#D4AF37]" />
                    {o.phoneDisplay}
                  </a>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <a data-testid="area-page-a-1" href={o.maps} target="_blank" rel="noopener noreferrer" className="text-xs text-center bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-bold py-2.5 rounded-lg hover:brightness-95 transition-all" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                      Directions ↗
                    </a>
                    <Link data-testid="area-page-link-3" to="/locations" className="text-xs text-center bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold py-2.5 rounded-lg transition-all" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                      All offices
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Optional body sections (state pages have richer body) */}
        {area.body && area.body.length > 0 && (
          <section className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto mb-16 space-y-10">
            {(area.body || []).map((b, i) => (
              <div key={i}>
                <h2 className="text-2xl text-white font-bold mb-3" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>{b.h}</h2>
                <p className="text-white/75 text-base leading-relaxed font-serif">{b.p}</p>
              </div>
            ))}
          </section>
        )}

        {/* Cities grid */}
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-16" data-testid="area-cities-grid">
          <div className="border-t border-white/10 pt-12">
            <h2 className="text-2xl sm:text-3xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Cities we serve
            </h2>
            <p className="text-white/55 text-sm mb-8 font-serif italic">
              {area.cities.length} named cities and towns. Don't see yours? Call — we work with clients in all 50 states.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-2">
              {/* index in the key: the city lists are hand-maintained per area and a name can repeat */}
              {(area.cities || []).map((city, ci) => (
                <div key={`${city}-${ci}`} className="flex items-center gap-2 text-white/80 text-sm py-1 group">
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37]/60 group-hover:bg-[#D4AF37] transition-colors flex-shrink-0"></span>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>{city}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Counties strip */}
        {area.counties && area.counties.length > 0 && (
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-16">
            <div className="bg-[#0F2A4D] border border-white/8 rounded-2xl p-6">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#D4AF37] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                Counties served
              </p>
              <p className="text-white/85 text-sm leading-relaxed font-serif">
                {area.counties.join(' · ')}
              </p>
            </div>
          </section>
        )}

        {/* FAQ */}
        {area.faqs && area.faqs.length > 0 && (
          <section className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto mt-20" data-testid="area-faq-section">
            <div className="mb-10 text-center">
              <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#D4AF37] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                Frequently Asked
              </p>
              <h2 className="text-3xl sm:text-4xl text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {area.type === 'state' ? `${area.state} questions, answered` : 'Local questions, answered'}<span className="text-[#D4AF37]">.</span>
              </h2>
            </div>
            <div className="space-y-3">
              {(area.faqs || []).map((f, i) => (
                <details key={i} className="group bg-[#0F2A4D] border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-colors" data-testid={`area-faq-${i}`}>
                  <summary data-testid="area-page-summary-1" className="flex items-start justify-between gap-4 px-6 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                    <span className="text-white font-semibold text-[15px] leading-snug pr-3">{f.q}</span>
                    <span className="flex-shrink-0 w-7 h-7 rounded-full border border-white/20 group-open:border-[#D4AF37]/60 flex items-center justify-center text-[#D4AF37] transition-all group-open:rotate-45">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-3.5 h-3.5">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6 pt-1 -mt-2">
                    <p className="text-white/75 text-[15px] leading-relaxed font-serif">{f.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Phone-first CTA */}
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mt-20">
          <div className="bg-gradient-to-br from-[#0F2A4D] to-[#0B2240] border border-[#D4AF37]/20 rounded-2xl p-10 sm:p-14 text-center">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/90 font-semibold mb-3" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
              Appointments by phone
            </p>
            <a href="tel:18445993355" className="inline-flex items-center gap-3 text-3xl sm:text-4xl font-bold text-white hover:text-[#D4AF37] transition-colors tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }} data-testid="area-call-cta">
              <Phone size={28} strokeWidth={2.2} className="text-[#D4AF37]" />
              1-844-599-3355
            </a>
            <p className="text-white/55 text-sm mt-3 font-serif italic">
              Mon – Fri · 9:00 AM – 6:00 PM EST · Sat 10 AM – 2 PM
            </p>
            <div className="flex justify-center gap-3 mt-7">
              <Link data-testid="area-page-link-2" to="/contact" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-[#D4AF37]/50 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-all" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                Or send a message
              </Link>
              <Link data-testid="area-page-link-1" to="/locations" className="inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold px-6 py-3 rounded-xl text-sm transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                View all offices <ArrowUpRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
